import type { BarcodeFormat } from '../schemas/item.js';

/**
 * GS1 mod-10 check digit for the DATA part of a code (i.e. everything except the
 * check digit itself). The rightmost data digit is weighted 3, then alternating —
 * which is what makes one function serve EAN-13, EAN-8 and UPC-A alike, since they
 * differ only in how many digits come before it.
 */
export function gs1CheckDigit(digits: string): string {
  let sum = 0;
  for (let i = digits.length - 1, w = 3; i >= 0; i--, w = w === 3 ? 1 : 3) sum += Number(digits[i]) * w;
  return String((10 - (sum % 10)) % 10);
}

/** Digits a code of this format carries in total, check digit included. */
const FIXED_LENGTH: Partial<Record<BarcodeFormat, number>> = { ean13: 13, ean8: 8, upc_a: 12 };

/**
 * What actually happens if `value` is printed as `format`. The two failure modes are
 * NOT the same problem, and lumping them together is how a shop ends up with a roll
 * of stickers that scan to nothing:
 *
 * - `unsupported` — the symbology refuses the value, so the renderer falls back to
 *   Code 128. The number on the sticker is still right; the label just isn't the
 *   type it claims. Annoying, recoverable.
 * - `renumbered` — MUCH worse, and invisible. JsBarcode accepts a TWELVE-digit
 *   EAN-13 and helpfully appends the check digit, so the sticker comes out carrying
 *   a THIRTEEN-digit number the product does not have. It scans perfectly and finds
 *   nothing at the till. (Verified in the browser: a 12-digit value really does
 *   render as a full EAN-13 rather than falling back.)
 * - `ok` — encodes exactly as stored: the right length, and a check digit that
 *   already agrees.
 *
 * Code 128 encodes any ASCII and `qr` is drawn as Code 128 by the label designer, so
 * both take anything non-empty. An empty value is `unsupported` — there is nothing to
 * encode; callers that only want to set a DEFAULT for future codes handle that case
 * themselves rather than asking this.
 */
export type BarcodeFit = 'ok' | 'renumbered' | 'unsupported';

export function barcodeFit(value: string | null | undefined, format: BarcodeFormat): BarcodeFit {
  const v = (value ?? '').trim();
  if (!v) return 'unsupported';
  const len = FIXED_LENGTH[format];
  if (len === undefined) return 'ok';               // code128 / qr — anything goes
  if (!/^\d+$/.test(v)) return 'unsupported';
  // One digit short of the symbology's width: the renderer computes the missing check
  // digit and prints a number that is not this product's barcode.
  if (v.length === len - 1) return 'renumbered';
  if (v.length !== len) return 'unsupported';
  return gs1CheckDigit(v.slice(0, -1)) === v.slice(-1) ? 'ok' : 'unsupported';
}

/** Prints as `format` and still says the same thing. The bar for changing data. */
export function isBarcodeValid(value: string | null | undefined, format: BarcodeFormat): boolean {
  return barcodeFit(value, format) === 'ok';
}

/**
 * A valid value for `format`: EAN/UPC get the '2' in-store prefix, a random body and
 * a computed check digit; code128/qr just get digits (any value is legal). Random,
 * so a rare collision surfaces as a duplicate-barcode error on save.
 */
export function generateBarcodeValue(format: BarcodeFormat): string {
  const randomDigits = (n: number): string => {
    let s = '';
    for (let i = 0; i < n; i++) s += Math.floor(Math.random() * 10);
    return s;
  };
  const len = FIXED_LENGTH[format];
  if (len === undefined) return randomDigits(12);
  const body = `2${randomDigits(len - 2)}`;
  return body + gs1CheckDigit(body);
}
